import React from 'react';
import {
  Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
  FormControlLabel, List, ListItem, ListItemText, Switch, TextField, Tooltip,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { patchDashboard } from '../../utilityFunctions/dashboardApi';

/** Normalizes the 422 body's `non_aggregated_widgets` (ids or {widget_id,title}) into display rows. */
const blockingRows = (data, widgets) => {
  const raw = (data && (data.non_aggregated_widgets || data.nonAggregatedWidgets)) || [];
  return raw.map((item) => {
    const id = item && typeof item === 'object' ? (item.widget_id || item.widgetId || item.id) : item;
    const w = (widgets || []).find((x) => x.widget_id === id);
    const title = (item && typeof item === 'object' && item.title) || (w && w.title) || String(id);
    return { id: String(id), title, reason: item && typeof item === 'object' ? item.reason : null };
  });
};

export function RenameDialog({ open, onClose, dashboard, jwt, onSaved }) {
  const { t } = useTranslation();
  const [title, setTitle] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    if (open) {
      setTitle((dashboard && dashboard.title) || '');
      setError(null);
    }
  }, [open, dashboard]);

  const save = async () => {
    const trimmed = title.trim();
    if (!trimmed) return;
    setSaving(true);
    const res = await patchDashboard(dashboard.id, { title: trimmed }, jwt);
    setSaving(false);
    if (res.ok) {
      onSaved(res.data);
      onClose();
    } else {
      setError(t('Dashboards.meta.renameFailed'));
    }
  };

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} fullWidth maxWidth="xs">
      <DialogTitle>{t('Dashboards.meta.renameTitle')}</DialogTitle>
      <DialogContent>
        <TextField autoFocus fullWidth margin="dense" label={t('Dashboards.meta.titleLabel')} value={title}
          inputProps={{ maxLength: 200 }}
          onChange={(e) => setTitle(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') save(); }} />
        {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button disabled={saving} onClick={onClose}>{t('Dashboards.common.cancel')}</Button>
        <Button variant="contained" disabled={saving || !title.trim()} onClick={save}>
          {saving ? <CircularProgress size={18} /> : t('Dashboards.common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Private ⇄ Tenant share switch (PATCH visibility). The server refuses TENANT
 * with 422 when a widget returns non-aggregated rows (H16); we list those widgets.
 */
export function ShareToggle({ dashboard, widgets, jwt, disabled, onSaved }) {
  const { t } = useTranslation();
  const [saving, setSaving] = React.useState(false);
  const [blocked, setBlocked] = React.useState(null);
  const [error, setError] = React.useState(null);
  const shared = dashboard && String(dashboard.visibility).toUpperCase() === 'TENANT';

  const toggle = async () => {
    setSaving(true);
    setError(null);
    const res = await patchDashboard(dashboard.id, { visibility: shared ? 'PRIVATE' : 'TENANT' }, jwt);
    setSaving(false);
    if (res.ok) onSaved(res.data);
    else if (res.status === 422) setBlocked(blockingRows(res.data, widgets));
    else setError(t('Dashboards.meta.shareFailed'));
  };

  return (
    <>
      <Tooltip title={shared ? t('Dashboards.meta.sharedHint') : t('Dashboards.meta.privateHint')}>
        <FormControlLabel
          sx={{ mr: 0 }}
          control={<Switch size="small" checked={shared} disabled={disabled || saving} onChange={toggle} />}
          label={shared ? t('Dashboards.meta.visibilityTenant') : t('Dashboards.meta.visibilityPrivate')}
        />
      </Tooltip>
      <Dialog open={!!blocked} onClose={() => setBlocked(null)} fullWidth maxWidth="sm">
        <DialogTitle>{t('Dashboards.meta.shareBlockedTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('Dashboards.meta.shareBlockedBody')}</DialogContentText>
          <List dense>
            {(blocked || []).map((b) => (
              <ListItem key={b.id}>
                <ListItemText primary={b.title} secondary={b.reason || t('Dashboards.meta.nonAggregated')} />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setBlocked(null)}>{t('Dashboards.common.ok')}</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={!!error} onClose={() => setError(null)}>
        <DialogContent><Alert severity="error">{error}</Alert></DialogContent>
        <DialogActions><Button onClick={() => setError(null)}>{t('Dashboards.common.ok')}</Button></DialogActions>
      </Dialog>
    </>
  );
}
